import React, { useState } from 'react';
import { useFormStatus } from '../hooks/useFormStatus';
import { submitProject } from '../utils/projects';
import { supabase } from '../lib/supabase';
import PublicNav from '../components/PublicNav';
import Footer from '../components/Footer';

const Submission = () => {
  const { isOpen, loading, status } = useFormStatus('submission');
  const [formData, setFormData] = useState({
    leader_email: '',
    short_description: '',
    github_url: '',
    demo_url: ''
  });
  const [teamValidation, setTeamValidation] = useState({
    isValid: false,
    message: '',
    loading: false
  });
  const [presentationUrl, setPresentationUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (name === 'leader_email') {
      setTeamValidation({ isValid: false, message: '', loading: false });
    }
  };

  const validateTeam = async () => {
    if (!formData.leader_email) return;

    setTeamValidation({ isValid: false, message: '', loading: true });

    try {
      const { data, error } = await supabase
        .from('teams')
        .select(`
          id,
          team_name,
          leader:applicants!teams_leader_id_fkey!inner(email)
        `)
        .eq('leader.email', formData.leader_email.trim())
        .single();

      if (error || !data) {
        setTeamValidation({
          isValid: false,
          message: 'لم يتم العثور على فريق مسجل بهذا البريد الإلكتروني',
          loading: false
        });
        return false;
      }

      setTeamValidation({
        isValid: true,
        message: `تم التحقق من الفريق: ${data.team_name}`,
        loading: false
      });
      return data.id;

    } catch (err) {
      console.error('Error validating team:', err);
      setTeamValidation({
        isValid: false,
        message: 'حدث خطأ أثناء التحقق من الفريق',
        loading: false
      });
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate team before submitting
    const teamId = await validateTeam();
    if (!teamId) {
        return;
    }

    setSubmitting(true);
    setMessage('');

    try {
      // Submit project with file URLs
      setMessage('جاري حفظ البيانات...');
      const result = await submitProject({
        ...formData,
        team_id: teamId,
        additional_files: presentationUrl // Store URL directly
      });

      setMessage(result.message);

      if (result.success) {
        setSubmitted(true);
      }
    } catch (err) {
      console.error('Error submitting project:', err);
      setMessage('حدث خطأ أثناء تقديم المشروع');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#05339c]">
        <PublicNav />
        <div className="flex justify-center items-center min-h-[80vh]">
          <div className="text-lg text-white">جاري التحميل...</div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!isOpen) {
    return (
      <div className="min-h-screen bg-[#05339c]">
        <PublicNav />
        <div className="flex justify-center items-center min-h-[80vh]">
          <div className="text-center bg-dark-blue p-8 rounded-2xl shadow-2xl max-w-md mx-4 border-2 border-yellow">
            <h1 className="text-2xl font-semibold text-yellow mb-4">فترة تقديم المشاريع مغلقة</h1>
            <p className="text-white">حالة النموذج: {status}</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Success page after submission
  if (submitted) {
    return (
      <div className="min-h-screen bg-[#05339c]">
        <PublicNav />
        <div className="flex justify-center items-center min-h-[80vh] p-6">
          <div className="bg-dark-blue rounded-2xl shadow-2xl p-12 border-2 border-green-500 max-w-md w-full text-center">
            <div className="mb-6 flex justify-center">
              <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center border-4 border-green-500">
                <svg className="w-12 h-12 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
            <h2 className="text-3xl font-bold text-green-400 mb-4">تم التقديم بنجاح!</h2>
            <p className="text-white text-lg ">{message}</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#05339c]">
      <PublicNav />
      <div className="max-w-2xl mx-auto p-6 py-32">
        <div className="bg-dark-blue rounded-2xl shadow-2xl p-8 border-2 border-yellow">
          <h1 className="text-3xl font-semibold text-center mb-8 text-white">
            تقديم <span className="text-yellow">المشروع</span>
          </h1>
        
        {message && !submitted && (
          <div className="mb-6 p-4 rounded-lg flex items-start gap-3 bg-red-500/20 border-2 border-red-500 text-red-400">
            <span className="flex-1">{message}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-yellow mb-2">
              البريد الإلكتروني لقائد الفريق *
            </label>
            <input
              type="email"
              name="leader_email"
              value={formData.leader_email}
              onChange={handleInputChange}
              onBlur={() => {
                if (formData.leader_email) validateTeam();
              }}
              required
              dir="ltr"
              className="w-full px-3 py-2 bg-blue/20 border border-yellow rounded-md focus:outline-none focus:ring-2 focus:ring-yellow text-white placeholder-gray-400"
              placeholder="email@example.com"
            />
          </div>
        </div>

        {/* Validation Message */}
        {(teamValidation.message || teamValidation.loading) && (
          <div className={`text-sm p-3 rounded-md ${
            teamValidation.loading ? 'bg-yellow/20 text-yellow' :
            teamValidation.isValid ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
          }`}>
            {teamValidation.loading ? 'جاري التحقق...' : teamValidation.message}
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-yellow mb-2">
            وصف مختصر للمشروع *
          </label>
          <textarea
            name="short_description"
            value={formData.short_description}
            onChange={handleInputChange}
            required
            rows="4"
            className="w-full px-3 py-2 bg-blue/20 border border-yellow rounded-md focus:outline-none focus:ring-2 focus:ring-yellow text-white placeholder-gray-400"
            placeholder="اشرح مشروعك باختصار..."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-yellow mb-2">
            رابط GitHub (الكود) *
          </label>
          <input
dir="ltr"            type="url"
            name="github_url"
            value={formData.github_url}
            onChange={handleInputChange}
            required
            className="w-full px-3 py-2 bg-blue/20 border border-yellow rounded-md focus:outline-none focus:ring-2 focus:ring-yellow text-white placeholder-gray-400"
            placeholder="https://github.com/username/repository"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-yellow mb-2">
            رابط الديمو
          </label>
          <input
dir="ltr"            type="url"
            name="demo_url"
            value={formData.demo_url}
            onChange={handleInputChange}
            className="w-full px-3 py-2 bg-blue/20 border border-yellow rounded-md focus:outline-none focus:ring-2 focus:ring-yellow text-white placeholder-gray-400"
            placeholder="https://your-demo.com"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-yellow mb-2">
            رابط فيديو العرض التقديمي *
          </label>
          <div>
              <input
              type="url"
              value={presentationUrl}
              onChange={(e) => setPresentationUrl(e.target.value)}
              required
              dir="ltr"
              className="w-full px-3 py-2 bg-blue/20 border border-yellow rounded-md focus:outline-none focus:ring-2 focus:ring-yellow text-white placeholder-gray-400"
              placeholder="https://youtube.com/..."
            />
          </div>
        </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-yellow text-dark-blue py-3 px-4 rounded-md hover:bg-yellow/80 focus:outline-none focus:ring-2 focus:ring-yellow disabled:opacity-50 transition-all duration-300 font-bold shadow-lg"
          >
            {submitting ? 'جاري الإرسال...' : 'تقديم المشروع'}
          </button>
        </form>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Submission;
